// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import React, { useState } from "react";
import Button from "@cloudscape-design/components/button";
import Container from "@cloudscape-design/components/container";
import Header from "@cloudscape-design/components/header";
import SpaceBetween from "@cloudscape-design/components/space-between";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import type { ILocalAgentClient } from "../../services/localAgentClient";
import { LocalAgentError } from "../../services/localAgentClient";
import { appConfig } from "../../config";
import type { WizardStepProps } from "../EnrollmentWizard";
import { useAuth } from "../../auth/CognitoAuthProvider";
import { type IWebCsrValidator, WebCsrValidator } from "../csrValidator";
import PinDialog from "../PinDialog";

export interface GenerateKeyStepProps extends WizardStepProps {
  localAgentClient: ILocalAgentClient;
  csrValidator?: IWebCsrValidator;
}

/**
 * Decodes the payload of a JWT (ID token) to extract claims.
 * Uses simple base64 decoding — no library needed.
 */
function decodeIdTokenPayload(idToken: string): Record<string, unknown> {
  const parts = idToken.split(".");
  if (parts.length !== 3) {
    throw new Error("Invalid ID token format");
  }
  const payload = atob(parts[1]);
  return JSON.parse(payload) as Record<string, unknown>;
}

/**
 * Extracts the username from the ID token claims.
 * Tries "name" first, then falls back to "cognito:username".
 */
function extractUsername(claims: Record<string, unknown>): string {
  const name = claims["name"] ?? claims["cognito:username"];
  if (typeof name !== "string" || name.length === 0) {
    throw new Error("Unable to extract username from ID token");
  }
  return name;
}

export default function GenerateKeyStep({
  state,
  setState,
  onComplete,
  onError,
  localAgentClient,
  csrValidator: csrValidatorProp,
}: GenerateKeyStepProps): React.ReactElement {
  const [loading, setLoading] = useState(false);
  const [pinDialogVisible, setPinDialogVisible] = useState(false);
  const [pinErrorMessage, setPinErrorMessage] = useState<string | undefined>();
  const { tokens } = useAuth();
  const csrValidator = csrValidatorProp ?? new WebCsrValidator();

  const doGenerate = async (pin?: string) => {
    setLoading(true);
    try {
      // Extract username from ID token
      if (!tokens?.idToken) {
        throw new Error("Not authenticated. Please log in and try again.");
      }
      const claims = decodeIdTokenPayload(tokens.idToken);
      const username = extractUsername(claims);

      // Determine if overwrite is needed based on wizard state
      // The ConnectYubiKeyStep already handled the overwrite confirmation
      const confirmOverwrite = true;

      // Call local agent to generate key
      const response = await localAgentClient.generateKey({
        algorithm: appConfig.keyAlgorithm,
        subject_cn: username,
        confirm_overwrite: confirmOverwrite,
        ...(pin !== undefined ? { pin } : {}),
      });

      // Validate the CSR
      const expectedSerial = state.yubiKeySerial;
      if (!expectedSerial) {
        throw new Error("YubiKey serial not available. Please go back and reconnect.");
      }

      csrValidator.validateCsr(response.csr_pem, username, expectedSerial);

      // Store CSR in wizard state and complete
      setState((prev) => ({
        ...prev,
        csrPem: response.csr_pem,
      }));
      onComplete();
    } catch (err) {
      if (err instanceof LocalAgentError) {
        if (err.errorCode === "PIN_CANCELLED") {
          onError("PIN entry was cancelled. Please try again and enter your YubiKey PIV PIN when prompted by the native dialog.");
          return;
        }
        if (err.errorCode === "PIN_REQUIRED") {
          setPinErrorMessage(undefined);
          setPinDialogVisible(true);
          return;
        }
        if (err.errorCode === "PIN_FAILED") {
          const retries = err.retriesRemaining;
          const msg =
            retries !== undefined
              ? `PIN verification failed. ${retries} ${retries === 1 ? "retry" : "retries"} remaining.`
              : "PIN verification failed.";
          setPinErrorMessage(msg);
          setPinDialogVisible(true);
          return;
        }
      }
      onError(err instanceof Error ? err.message : "Key generation failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = () => void doGenerate();

  const handlePinSubmit = (pin: string) => {
    setPinDialogVisible(false);
    setPinErrorMessage(undefined);
    void doGenerate(pin);
  };

  const handlePinCancel = () => {
    setPinDialogVisible(false);
    setPinErrorMessage(undefined);
    onError("PIN is required to proceed");
  };

  if (loading) {
    return (
      <Container>
        <StatusIndicator type="loading">
          Generating key pair on YubiKey…
        </StatusIndicator>
      </Container>
    );
  }

  return (
    <SpaceBetween size="m">
      <Container header={<Header variant="h3">Generate Key Pair</Header>}>
        <SpaceBetween size="s">
          <p>
            A new ECC P-256 key pair will be generated in PIV slot 9a on your
            YubiKey. A Certificate Signing Request (CSR) will be created and
            validated before proceeding.
          </p>
          <Button
            variant="primary"
            onClick={handleGenerate}
            loading={loading}
          >
            Generate Key
          </Button>
        </SpaceBetween>
      </Container>
      <PinDialog
        visible={pinDialogVisible}
        onSubmit={handlePinSubmit}
        onCancel={handlePinCancel}
        errorMessage={pinErrorMessage}
      />
    </SpaceBetween>
  );
}
